import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  CreditCard,
  Users,
  Shield,
  Palette,
  Globe,
  MessageSquare,
  Smartphone,
  Settings2,
  Database,
  Link2,
  Lock,
  FileText,
  FlaskConical,
  AlertTriangle,
  Eye,
  Sparkles,
  Save,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';

// Section Components
import AgencyProfileSection from '../../components/platform/settings/AgencyProfileSection';
import BillingMembershipSection from '../../components/platform/settings/BillingMembershipSection';
import MyStaffSection from '../../components/platform/settings/MyStaffSection';
import PortalAccessSection from '../../components/platform/settings/PortalAccessSection';
import PortalsMobileAppSection from '../../components/platform/settings/PortalsMobileAppSection';
import BrandingWhiteLabelSection from '../../components/platform/settings/BrandingWhiteLabelSection';
import DomainsRedirectsSection from '../../components/platform/settings/DomainsRedirectsSection';
import CommunicationSettingsSection from '../../components/platform/settings/CommunicationSettingsSection';
import CustomFieldsSection from '../../components/platform/settings/CustomFieldsSection';
import CustomValuesSection from '../../components/platform/settings/CustomValuesSection';
import IntegrationsSection from '../../components/platform/settings/IntegrationsSection';
import PrivateIntegrationsSection from '../../components/platform/settings/PrivateIntegrationsSection';
import AuditLogsSection from '../../components/platform/settings/AuditLogsSection';
import AdvancedLabsSection from '../../components/platform/settings/AdvancedLabsSection';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Section labels for display
const SECTION_LABELS = {
  'profile': 'Agency Profile',
  'billing': 'Billing & Membership',
  'staff': 'My Staff',
  'portal-access': 'Portal Access',
  'portals-mobile': 'Portals & Mobile App',
  'branding': 'Branding & White Label',
  'domains': 'Domains & Redirects',
  'communication': 'Communication Settings',
  'custom-fields': 'Custom Fields',
  'custom-values': 'Custom Values',
  'integrations': 'Integrations',
  'private-integrations': 'Private Integrations',
  'audit-logs': 'Audit Logs',
  'labs': 'Advanced / Labs',
};

/**
 * Locked Section Overlay - shown when section is locked by platform admin
 */
const LockedSectionOverlay = ({ sectionKey, sectionLabel }) => (
  <div className="flex flex-col items-center justify-center py-16 px-8 text-center" data-testid={`locked-section-${sectionKey}`}>
    <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mb-6">
      <Lock className="text-slate-400" size={36} />
    </div>
    <h3 className="text-xl font-semibold text-slate-800 mb-2">
      {sectionLabel || 'This Section'} is Locked
    </h3>
    <p className="text-slate-500 max-w-md mb-6">
      Access to this section has been restricted by your platform administrator. 
      This may be due to your current plan or agency configuration.
    </p>
    <div className="flex items-center gap-3">
      <Button 
        className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
        data-testid="upgrade-add-features-btn"
      >
        <Sparkles size={16} className="mr-2" />
        Upgrade / Add Features
      </Button>
      <Button variant="outline" className="text-slate-600">
        Contact Support
      </Button>
    </div>
  </div>
);

/**
 * Hidden Section Handler - shown when navigating to a hidden section via direct URL
 */
const HiddenSectionHandler = ({ sectionKey, onNavigate }) => (
  <div className="flex flex-col items-center justify-center py-16 px-8 text-center" data-testid={`hidden-section-${sectionKey}`}>
    <div className="w-20 h-20 rounded-2xl bg-red-50 flex items-center justify-center mb-6">
      <AlertTriangle className="text-red-400" size={36} />
    </div>
    <h3 className="text-xl font-semibold text-slate-800 mb-2">
      Section Not Available
    </h3>
    <p className="text-slate-500 max-w-md mb-6">
      This section is not available in your current configuration. 
      Please contact your platform administrator if you believe this is an error.
    </p>
    <Button 
      variant="outline" 
      onClick={() => onNavigate('profile')}
      className="text-slate-600"
    >
      <ArrowLeft size={16} className="mr-2" />
      Return to Agency Profile
    </Button>
  </div>
);

/**
 * Read-Only Banner - shown at top of read-only sections
 */
const ReadOnlyBanner = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6 flex items-center gap-3" data-testid="read-only-banner">
    <Eye className="text-blue-500" size={18} />
    <div>
      <p className="text-sm font-medium text-blue-800">View Only Mode</p>
      <p className="text-xs text-blue-600">
        This section is read-only. Contact your platform administrator to make changes.
      </p>
    </div>
  </div>
);

/**
 * Agency Settings Workspace - Agency Owner Console
 * 
 * Agency-scoped settings experience with:
 * - Internal settings sidebar with grouped sections
 * - Entitlement-gated visibility
 * - Support mode read-only behavior
 * - Premium FundElite styling
 * 
 * IMPORTANT: This is agency-scoped, not platform-global.
 * Agency owner can manage their own agency but not the whole platform.
 */
const AgencySettingsPage = () => {
  const { agencyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { supportContext, isInSupportMode } = useSupportContext();
  
  const [agency, setAgency] = useState(null);
  const [entitlements, setEntitlements] = useState({});
  const [settingsControl, setSettingsControl] = useState({});
  const [internalOverrides, setInternalOverrides] = useState({});
  const [isInternalAgency, setIsInternalAgency] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Active section from URL or default to 'profile'
  const activeSection = searchParams.get('section') || 'profile';
  
  // Determine if read-only mode (support without edit permission)
  const isReadOnly = isInSupportMode && supportContext?.is_read_only !== false;
  
  // Is platform owner or super admin?
  const isPlatformAdmin = user?.role === 'platform_owner' || user?.role === 'super_admin';
  
  const headers = { Authorization: `Bearer ${token}` };
  
  // Fetch agency data, entitlements, and settings control
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch agency details
        const agencyRes = await axios.get(`${API}/owner-console/agencies/${agencyId}`, { headers });
        setAgency(agencyRes.data);
        
        // Fetch agency entitlements/membership
        try {
          const membershipRes = await axios.get(`${API}/admin/agencies/${agencyId}/membership`, { headers });
          const membership = membershipRes.data;
          
          // Build entitlements map from plan
          const entMap = {};
          if (membership?.plan_entitlements) {
            membership.plan_entitlements.forEach(ent => {
              entMap[ent.entitlement_key] = ent.access_mode || 'full_access';
            });
          }
          setEntitlements(entMap);
        } catch (err) {
          // Default entitlements if membership fetch fails
          setEntitlements({});
        }
        
        // Fetch settings control (what platform admin allows)
        try {
          const controlRes = await axios.get(`${API}/admin/agencies/${agencyId}/settings-control`, { headers });
          setSettingsControl(controlRes.data?.settings_control || {});
        } catch (err) {
          // Use defaults if settings control fetch fails
          setSettingsControl({});
        }
        
        // Fetch internal overrides (for internal agency test access)
        try {
          const overridesRes = await axios.get(`${API}/admin/agencies/${agencyId}/internal-overrides`, { headers });
          setIsInternalAgency(overridesRes.data?.is_internal || false);
          setInternalOverrides(overridesRes.data?.internal_overrides || {});
        } catch (err) {
          setIsInternalAgency(false);
          setInternalOverrides({});
        }
        
      } catch (err) {
        console.error('Failed to fetch agency settings:', err);
        setError(err.response?.data?.detail || 'Failed to load agency settings');
      } finally {
        setLoading(false);
      }
    };
    
    if (token && agencyId) {
      fetchData();
    }
  }, [token, agencyId]);
  
  // Navigate to section
  const navigateToSection = (sectionKey) => {
    setSearchParams({ section: sectionKey });
  };
  
  // Section key to settings control key mapping
  const sectionToControlKey = {
    'profile': 'agency_profile',
    'billing': 'billing_membership',
    'staff': 'my_staff',
    'portal-access': 'portal_access',
    'portals-mobile': 'portals_mobile_app',
    'branding': 'branding_white_label',
    'domains': 'domains_redirects',
    'communication': 'communication_settings',
    'custom-fields': 'custom_fields',
    'custom-values': 'custom_values',
    'integrations': 'integrations',
    'private-integrations': 'private_integrations',
    'audit-logs': 'audit_logs',
    'labs': 'advanced_labs',
  };
  
  // Get settings control mode for a section
  const getControlMode = (sectionKey) => {
    const controlKey = sectionToControlKey[sectionKey];
    return settingsControl[controlKey] || 'editable'; // Default to editable if not set
  };
  
  // Check if section is visible (not hidden)
  const isSectionVisible = (sectionKey) => {
    if (isPlatformAdmin) return true; // Platform admins see everything
    const mode = getControlMode(sectionKey);
    return mode !== 'hidden';
  };
  
  // Check if section is editable
  const isSectionEditable = (sectionKey) => {
    if (isPlatformAdmin) return true; // Platform admins can always edit
    const mode = getControlMode(sectionKey);
    return mode === 'editable';
  };
  
  // Check if section is read-only (visible but not editable)
  const isSectionReadOnly = (sectionKey) => {
    if (isPlatformAdmin) return false;
    const mode = getControlMode(sectionKey);
    return mode === 'read_only';
  };
  
  // Check if section is locked (requires upgrade)
  const isSectionLocked = (sectionKey) => {
    if (isPlatformAdmin) return false;
    const mode = getControlMode(sectionKey);
    return mode === 'locked';
  };
  
  // Get badge for section based on control mode
  const getSectionBadge = (sectionKey) => {
    if (isPlatformAdmin) return null;
    const mode = getControlMode(sectionKey);
    if (mode === 'locked') return 'locked';
    if (mode === 'read_only') return 'read_only';
    if (mode === 'hidden') return null; // Won't show anyway
    return null;
  };
  
  // Settings sidebar structure - following exact HTML spec
  const settingsSections = [
    {
      group: 'General',
      items: [
        { key: 'profile', label: 'Agency Profile', icon: Building2 },
        { key: 'billing', label: 'Billing & Membership', icon: CreditCard },
        { key: 'staff', label: 'My Staff', icon: Users },
      ],
    },
    {
      group: 'Portal & Branding',
      items: [
        { key: 'portal-access', label: 'Portal Access', icon: Shield },
        { key: 'portals-mobile', label: 'Portals & Mobile App', icon: Smartphone },
        { key: 'branding', label: 'Branding & White Label', icon: Palette },
        { key: 'domains', label: 'Domains & Redirects', icon: Globe },
      ],
    },
    {
      group: 'Communication & Services',
      items: [
        { key: 'communication', label: 'Communication Settings', icon: MessageSquare },
      ],
    },
    {
      group: 'Data & System',
      items: [
        { key: 'custom-fields', label: 'Custom Fields', icon: Database },
        { key: 'custom-values', label: 'Custom Values', icon: Settings2 },
        { key: 'integrations', label: 'Integrations', icon: Link2 },
        { key: 'private-integrations', label: 'Private Integrations', icon: Lock },
      ],
    },
    {
      group: 'Governance',
      items: [
        { key: 'audit-logs', label: 'Audit Logs', icon: FileText },
        { key: 'labs', label: 'Advanced / Labs', icon: FlaskConical, restricted: true },
      ],
    },
  ];
  
  // Render active section content
  const renderSectionContent = () => {
    const sectionLabel = SECTION_LABELS[activeSection] || 'Settings';
    
    // Handle HIDDEN sections - block direct URL access for non-admins
    if (!isPlatformAdmin && !isSectionVisible(activeSection)) {
      return <HiddenSectionHandler sectionKey={activeSection} onNavigate={navigateToSection} />;
    }
    
    // Handle LOCKED sections - show locked overlay with upgrade CTA
    if (!isPlatformAdmin && isSectionLocked(activeSection)) {
      return <LockedSectionOverlay sectionKey={activeSection} sectionLabel={sectionLabel} />;
    }
    
    // Determine if section is read-only (includes support mode)
    const sectionIsReadOnly = isReadOnly || (!isPlatformAdmin && isSectionReadOnly(activeSection));
    
    const commonProps = {
      agency,
      agencyId,
      entitlements,
      isReadOnly: sectionIsReadOnly,
      isPlatformAdmin,
      internalOverrides,
      isInternalAgency,
      onUpdate: (updatedAgency) => setAgency({ ...agency, ...updatedAgency }),
      navigateToSection,
    };
    
    // Wrap content with read-only banner if applicable
    const wrapWithReadOnlyBanner = (content) => {
      if (sectionIsReadOnly && !isReadOnly) {
        // Show banner only for settings-control read-only, not support mode (which has its own indicator)
        return (
          <>
            <ReadOnlyBanner />
            {content}
          </>
        );
      }
      return content;
    };
    
    switch (activeSection) {
      case 'profile':
        return wrapWithReadOnlyBanner(<AgencyProfileSection {...commonProps} />);
      case 'billing':
        return wrapWithReadOnlyBanner(<BillingMembershipSection {...commonProps} />);
      case 'staff':
        return wrapWithReadOnlyBanner(<MyStaffSection {...commonProps} />);
      case 'portal-access':
        return wrapWithReadOnlyBanner(<PortalAccessSection {...commonProps} />);
      case 'portals-mobile':
        return wrapWithReadOnlyBanner(<PortalsMobileAppSection {...commonProps} />);
      case 'branding':
        return wrapWithReadOnlyBanner(<BrandingWhiteLabelSection {...commonProps} />);
      case 'domains':
        return wrapWithReadOnlyBanner(<DomainsRedirectsSection {...commonProps} />);
      case 'communication':
        return wrapWithReadOnlyBanner(<CommunicationSettingsSection {...commonProps} />);
      case 'custom-fields':
        return wrapWithReadOnlyBanner(<CustomFieldsSection {...commonProps} />);
      case 'custom-values':
        return wrapWithReadOnlyBanner(<CustomValuesSection {...commonProps} />);
      case 'integrations':
        return wrapWithReadOnlyBanner(<IntegrationsSection {...commonProps} />);
      case 'private-integrations':
        return wrapWithReadOnlyBanner(<PrivateIntegrationsSection {...commonProps} />);
      case 'audit-logs':
        return wrapWithReadOnlyBanner(<AuditLogsSection {...commonProps} />);
      case 'labs':
        return wrapWithReadOnlyBanner(<AdvancedLabsSection {...commonProps} />);
      default:
        return wrapWithReadOnlyBanner(<AgencyProfileSection {...commonProps} />);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 flex items-center gap-3">
          <AlertTriangle size={24} />
          <div>
            <p className="font-medium">Failed to load agency settings</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="agency-settings-page">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700 sticky top-0 z-10">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Support Mode Indicator */}
            <div className="flex items-center gap-4">
              {isInSupportMode && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full">
                  <Eye size={14} className="text-amber-400" />
                  <span className="text-amber-300 text-sm font-medium">Support Mode</span>
                  {isReadOnly && (
                    <span className="text-amber-400/70 text-xs">Read Only</span>
                  )}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/platform/agencies/${agencyId}`)}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Agency
              </Button>
              {!isReadOnly && (
                <Button
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Save size={16} className="mr-2" />
                  Save Changes
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Settings Shell */}
      <div className="p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200">
          {/* Shell Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                  <Settings2 className="text-white" size={24} />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900">
                    {agency?.name || 'Agency'} Settings
                  </h1>
                  <p className="text-sm text-slate-500">
                    Configure agency settings and preferences
                  </p>
                </div>
              </div>
              
              <Badge className="bg-emerald-100 text-emerald-700 border-0 px-3 py-1.5">
                <Sparkles size={14} className="mr-1.5" />
                Agency Owner Workspace
              </Badge>
            </div>
          </div>
          
          {/* Settings Body */}
          <div className="flex min-h-[calc(100vh-220px)]">
            {/* Internal Settings Sidebar */}
            <aside className="w-72 shrink-0 bg-gradient-to-b from-slate-50 to-slate-100/50 border-r border-slate-200">
              {/* Back Link */}
              <div className="p-4 border-b border-slate-200">
                <button
                  onClick={() => navigate(`/platform/agencies/${agencyId}`)}
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-amber-600 transition-colors"
                >
                  <ArrowLeft size={16} />
                  <span>Back to Agency</span>
                </button>
              </div>
              
              {/* Navigation Groups */}
              <nav className="p-4 space-y-6">
                {settingsSections.map((section) => {
                  // Filter items based on visibility (settingsControl mode) and restrictions
                  const visibleItems = section.items.filter(item => {
                    // Restricted sections (like Labs) only for platform admins
                    if (item.restricted && !isPlatformAdmin) return false;
                    // Check settingsControl visibility - hidden sections don't appear for non-admins
                    if (!isSectionVisible(item.key)) return false;
                    return true;
                  });
                  
                  if (visibleItems.length === 0) return null;
                  
                  return (
                    <div key={section.group}>
                      <h4 className="px-3 mb-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
                        {section.group}
                      </h4>
                      <ul className="space-y-1">
                        {visibleItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = activeSection === item.key;
                          const isLocked = isSectionLocked(item.key);
                          const isReadOnlySection = isSectionReadOnly(item.key);
                          const badge = getSectionBadge(item.key);
                          
                          return (
                            <li key={item.key}>
                              <button
                                onClick={() => navigateToSection(item.key)}
                                className={cn(
                                  'w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                                  isActive
                                    ? 'bg-white text-amber-700 shadow-sm border border-amber-200'
                                    : isLocked
                                    ? 'text-slate-400 hover:bg-white/50'
                                    : 'text-slate-600 hover:bg-white/80 hover:text-slate-900'
                                )}
                                data-testid={`settings-nav-${item.key}`}
                              >
                                <div className="flex items-center gap-3">
                                  <Icon size={18} className={isActive ? 'text-amber-600' : isLocked ? 'text-slate-400' : ''} />
                                  <span>{item.label}</span>
                                </div>
                                {badge && (
                                  <span className={cn(
                                    'px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full',
                                    badge === 'locked' 
                                      ? 'bg-slate-200 text-slate-500' 
                                      : badge === 'read_only'
                                      ? 'bg-blue-100 text-blue-600'
                                      : 'bg-amber-100 text-amber-600'
                                  )}>
                                    {badge === 'read_only' ? 'VIEW' : badge.toUpperCase()}
                                  </span>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </nav>
            </aside>
            
            {/* Main Content Area — scrollable so long profile sections (password mgmt) are reachable */}
            <main className="flex-1 min-h-0 min-w-0 bg-white overflow-y-auto">
              {/* Access Order Banner */}
              <div className="px-6 py-3 bg-gradient-to-r from-slate-50 to-amber-50/30 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      Access order: Plan entitlement → Agency settings access → Role permission
                    </p>
                    <p className="text-xs text-slate-500">
                      This workspace is agency-scoped. Platform-global controls are intentionally excluded here.
                    </p>
                  </div>
                  {isReadOnly && (
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300">
                      Support Read Only
                    </Badge>
                  )}
                </div>
              </div>
              
              {/* Section Content */}
              <div className="p-6 pb-12">
                {renderSectionContent()}
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencySettingsPage;
